import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import type { Page } from '../api/types';

export type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

export interface AsyncState<T> {
  status: AsyncStatus;
  data: T | null;
  error: ApiError | null;
  reload: () => void;
}

/** Runs an async loader on mount and whenever `deps` change. */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tick, setTick] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    setError(null);
    loaderRef
      .current()
      .then((res) => {
        if (!alive) return;
        setData(res);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
        setStatus('error');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { status, data, error, reload };
}

export interface PaginatedState<T> {
  status: AsyncStatus;
  items: T[];
  error: ApiError | null;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

/**
 * Cursor-based, server-side pagination. `fetchPage(cursor)` returns a Page<T>.
 * `keyDeps` reset the list (e.g. a changed search query or archive selection).
 */
export function usePaginated<T>(
  fetchPage: (cursor: string | null) => Promise<Page<T>>,
  keyDeps: unknown[],
): PaginatedState<T> {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [tick, setTick] = useState(0);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  // Initial / reset load whenever keyDeps change.
  useEffect(() => {
    let alive = true;
    setStatus('loading');
    setError(null);
    setItems([]);
    setCursor(null);
    setHasMore(false);
    fetchRef
      .current(null)
      .then((page) => {
        if (!alive) return;
        setItems(page.items ?? []);
        setCursor(page.nextCursor ?? null);
        setHasMore(Boolean(page.nextCursor));
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
        setStatus('error');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...keyDeps, tick]);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    fetchRef
      .current(cursor)
      .then((page) => {
        setItems((prev) => [...prev, ...(page.items ?? [])]);
        setCursor(page.nextCursor ?? null);
        setHasMore(Boolean(page.nextCursor));
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
      })
      .finally(() => setLoadingMore(false));
  }, [cursor, loadingMore]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { status, items, error, hasMore, loadingMore, loadMore, reload };
}
